import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

import { COLOR_SCHEME_ATTRIBUTE } from "@/src/theme/colorSchemeBootstrap";

type ColorScheme = "light" | "dark";

type StyleObject = Record<string, unknown>;

const isStyleObject = (value: unknown): value is StyleObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const schemeThemeCache = new WeakMap<Theme, Record<ColorScheme, Theme>>();

/** Themes {@link themeForScheme} has already resolved to one scheme. */
const resolvedSchemeThemes = new WeakSet<Theme>();

/**
 * The app theme resolved to one color scheme, whichever scheme is active.
 *
 * Mirrors how `ThemeProvider` merges `theme.colorSchemes[scheme]` into the
 * theme it hands out, so `theme.palette` and {@link isDarkMode} read the
 * requested scheme rather than the reader's.
 */
export const themeForScheme = (theme: Theme, scheme: ColorScheme): Theme => {
  let schemes = schemeThemeCache.get(theme);

  if (!schemes) {
    const resolve = (key: ColorScheme): Theme => {
      const colorScheme = theme.colorSchemes?.[key] as StyleObject | undefined;
      if (!colorScheme) return theme;

      const resolved: StyleObject = { ...theme };
      for (const [schemeKey, value] of Object.entries(colorScheme)) {
        const current = resolved[schemeKey];
        resolved[schemeKey] =
          isStyleObject(value) && isStyleObject(current)
            ? { ...current, ...value }
            : value;
      }
      resolvedSchemeThemes.add(resolved as unknown as Theme);
      return resolved as unknown as Theme;
    };

    schemes = { light: resolve("light"), dark: resolve("dark") };
    schemeThemeCache.set(theme, schemes);
  }

  return schemes[scheme];
};

/**
 * The parts of `dark` that differ from `light`, ready to layer on top of it.
 *
 * A key only `light` sets is reset with `unset` so dark mode does not inherit
 * it. Resets come first so a shorthand being dropped (`border`) cannot wipe a
 * longhand dark mode sets (`borderColor`).
 */
const darkOverrides = (light: StyleObject, dark: StyleObject): StyleObject => {
  const resets: StyleObject = {};
  const overrides: StyleObject = {};

  for (const [key, lightValue] of Object.entries(light)) {
    if (lightValue === undefined || dark[key] !== undefined) continue;
    if (isStyleObject(lightValue)) {
      const nested = darkOverrides(lightValue, {});
      if (Object.keys(nested).length > 0) resets[key] = nested;
    } else {
      resets[key] = "unset";
    }
  }

  for (const [key, darkValue] of Object.entries(dark)) {
    if (darkValue === undefined) continue;
    const lightValue = light[key];

    if (isStyleObject(darkValue)) {
      const nested = isStyleObject(lightValue)
        ? darkOverrides(lightValue, darkValue)
        : darkValue;
      if (Object.keys(nested).length > 0) overrides[key] = nested;
    } else if (
      !Object.is(lightValue, darkValue) &&
      JSON.stringify(lightValue) !== JSON.stringify(darkValue)
    ) {
      overrides[key] = darkValue;
    }
  }

  return { ...resets, ...overrides };
};

/** Deep merge of two style objects; `b` wins where both set a value. */
const mergeStyles = (a: StyleObject, b: StyleObject): StyleObject => {
  const merged: StyleObject = { ...a };
  for (const [key, value] of Object.entries(b)) {
    const current = merged[key];
    merged[key] =
      isStyleObject(current) && isStyleObject(value)
        ? mergeStyles(current, value)
        : value;
  }
  return merged;
};

/**
 * Styles that follow the reader's color scheme in pure CSS.
 *
 * `build` runs once per scheme against a theme resolved to that scheme, so it
 * can keep branching on {@link isDarkMode} and reading `theme.palette`. The
 * light result is the base and whatever dark mode changes is layered on under
 * `theme.applyStyles("dark", …)`, the `[data-mui-color-scheme="dark"]`
 * selector `InitColorSchemeScript` sets before the first paint.
 *
 * Use this instead of branching on the active scheme for anything a
 * prerendered page renders. The scheme is only known in the browser, so the
 * static HTML is built in light mode; styles that branch on the active scheme
 * paint light until React hydrates and re-renders, which a dark mode reader
 * sees as a flash. `scripts/verify-color-scheme.mjs` checks that the
 * prerendered pages do not depend on the scheme.
 *
 * Under `:where()` the dark selector adds no specificity, so dark rules win by
 * coming later in the same class, exactly like MUI's own `applyStyles`.
 */
export const schemeStyles = <T extends object>(
  theme: Theme,
  build: (schemeTheme: Theme) => T
): T => {
  // Called from inside another `schemeStyles` build, which is already running
  // once per scheme. Splitting again would hand the outer dark pass this
  // helper's light base, and nest its dark rules under a second dark selector
  // that never matches.
  if (resolvedSchemeThemes.has(theme)) return build(theme);

  const light = build(themeForScheme(theme, "light")) as StyleObject;
  const dark = build(themeForScheme(theme, "dark")) as StyleObject;
  const overrides = darkOverrides(light, dark);

  if (Object.keys(overrides).length === 0) return light as T;

  // `build` may already use `applyStyles("dark", …)` itself, under the same
  // key. Merge into that block rather than replacing it, and move it last so
  // its rules come after every light rule they override.
  const result: StyleObject = { ...light };
  for (const [selector, styles] of Object.entries(
    theme.applyStyles("dark", overrides) as StyleObject
  )) {
    const existing = result[selector];
    delete result[selector];
    result[selector] =
      isStyleObject(existing) && isStyleObject(styles)
        ? mergeStyles(existing, styles)
        : styles;
  }
  return result as T;
};

/**
 * {@link schemeStyles} as an `sx` callback.
 *
 * @example
 * <Box sx={schemeSx((theme) => ({
 *   backgroundColor: isDarkMode(theme) ? homePageHeroColors.heroBackground : theme.palette.brand.tertiary
 * }))} />
 */
export const schemeSx =
  (build: (schemeTheme: Theme) => SystemStyleObject<Theme>) =>
  (theme: Theme): SystemStyleObject<Theme> =>
    schemeStyles(theme, (schemeTheme) => build(schemeTheme) ?? {});

/**
 * Hides an element unless the reader is in `scheme`.
 *
 * For markup that differs by scheme, such as a logo with a white lockup for
 * dark mode: render one element per scheme and let CSS pick, so the
 * prerendered HTML carries both and shows the right one before React loads.
 * Anything not marked dark counts as light, so a page without the attribute
 * still shows exactly one variant.
 *
 * The selectors start with `*` for the same reason MUI's `applyStyles` does:
 * Emotion reads a nested selector that starts with `:` as a pseudo-class of
 * the element itself and prefixes it with `&`, which would never match.
 */
export const onlyInSchemeSx = (
  scheme: ColorScheme
): Record<string, SystemStyleObject<Theme>> => ({
  [scheme === "dark"
    ? `*:where(html:not([${COLOR_SCHEME_ATTRIBUTE}="dark"])) &`
    : `*:where(html[${COLOR_SCHEME_ATTRIBUTE}="dark"]) &`]: {
    display: "none"
  }
});
